import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Col, Empty, Input, Modal, Row, Select, Space, Tag,
  Tabs, Typography, message, Spin,
} from 'antd';
import {
  ApiOutlined,
  BookOutlined,
  BranchesOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  GlobalOutlined,
  LockOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
  ShopOutlined,
  StarFilled,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import * as AntdIcons from '@ant-design/icons';
import * as api from '../api/client';
import type { AgentRecord, AgentCategory, AgentType } from '../api/client';
import AgentBuilderWizard from './AgentBuilderWizard';
import AgentDetailView from './AgentDetailView';
import type { ProjectFolderSummary } from '../types';

const { Text, Title, Paragraph } = Typography;

const CATEGORY_COLORS: Record<string, string> = {
  SOFTWARE_ENGINEERING: 'blue',
  DEVOPS: 'cyan',
  AGILE: 'geekblue',
  PROJECT_MANAGEMENT: 'geekblue',
  GOVERNMENT_CONTRACTING: 'purple',
  BUSINESS_DEVELOPMENT: 'orange',
  SALES: 'gold',
  MARKETING: 'magenta',
  RESEARCH: 'lime',
  HR: 'green',
  FINANCE: 'volcano',
  OPERATIONS: 'orange',
  CUSTOM: 'default',
};

const CATEGORY_LABELS: Record<string, string> = {
  SOFTWARE_ENGINEERING: 'Software Engineering',
  DEVOPS: 'DevOps',
  AGILE: 'Agile',
  PROJECT_MANAGEMENT: 'Project Management',
  GOVERNMENT_CONTRACTING: 'Gov Contracting',
  BUSINESS_DEVELOPMENT: 'Business Dev',
  SALES: 'Sales',
  MARKETING: 'Marketing',
  RESEARCH: 'Research',
  HR: 'HR',
  FINANCE: 'Finance',
  OPERATIONS: 'Operations',
  CUSTOM: 'Custom',
};

const VISIBILITY_ICON: Record<string, React.ReactNode> = {
  PRIVATE: <LockOutlined style={{ color: '#6b7280' }} />,
  ORGANIZATION: <TeamOutlined style={{ color: '#6b7280' }} />,
  PUBLIC: <GlobalOutlined style={{ color: '#6b7280' }} />,
};

const TYPE_COLORS: Record<AgentType, string> = {
  KNOWLEDGE: 'blue',
  TASK: 'green',
  WORKFLOW: 'purple',
  API: 'geekblue',
};

const TYPE_LABELS: Record<AgentType, string> = {
  KNOWLEDGE: 'Knowledge',
  TASK: 'Task',
  WORKFLOW: 'Workflow',
  API: 'API',
};

/** Render an agent icon — supports antd icon names, data URLs, and emoji. */
export const renderAgentIcon = (icon: string, size = 28): React.ReactNode => {
  if (!icon) return <span style={{ fontSize: size }}>🤖</span>;
  if (icon.startsWith('data:') || icon.startsWith('http://') || icon.startsWith('https://')) {
    return (
      <img
        src={icon}
        alt="agent icon"
        style={{ width: size, height: size, borderRadius: 4, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  const IconComp = (AntdIcons as any)[icon] as React.ComponentType<any> | undefined;
  if (IconComp) {
    return <IconComp style={{ fontSize: size, color: '#6366f1' }} />;
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

interface Props {
  userId?: string;
  onOpenTask?: (taskId: string) => void;
  initialAgentId?: string;
  projectFolders?: ProjectFolderSummary[];
  onPrepareProjectFolders?: () => Promise<void>;
}

const AgentsView: React.FC<Props> = ({ userId = '', onOpenTask, initialAgentId, projectFolders, onPrepareProjectFolders }) => {
  const [activeTab, setActiveTab] = useState<'my' | 'shared' | 'marketplace'>('my');
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AgentCategory | ''>('');
  const [typeFilter, setTypeFilter] = useState<AgentType | ''>('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentRecord | null>(null);
  const [detailAgentId, setDetailAgentId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (initialAgentId) {
      setDetailAgentId(initialAgentId);
      setActiveTab('my');
    }
  }, [initialAgentId]);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'marketplace') {
        const res = await api.searchMarketplace({
          q: searchText,
          category: categoryFilter || undefined,
          agent_type: typeFilter || undefined,
        });
        setAgents(res.data.agents);
      } else {
        const res = await api.listAgents(activeTab as 'my' | 'shared' | 'subscribed');
        setAgents(res.data.agents);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchText, categoryFilter, typeFilter]);

  useEffect(() => { void loadAgents(); }, [loadAgents]);

  const handleDelete = async (agent: AgentRecord) => {
    setDeletingId(agent.id);
    try {
      await api.deleteAgent(agent.id);
      message.success(`Agent "${agent.name}" deleted`);
      void loadAgents();
    } catch {
      message.error('Failed to delete agent');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubscribe = async (agent: AgentRecord) => {
    try {
      await api.subscribeAgent(agent.id);
      message.success(`Installed "${agent.name}"`);
      void loadAgents();
    } catch {
      message.error('Failed to install agent');
    }
  };

  const handleFork = async (agent: AgentRecord) => {
    try {
      const res = await api.forkAgent(agent.id);
      message.success(`Forked as "${res.data.name}"`);
      setActiveTab('my');
    } catch {
      message.error('Failed to fork agent');
    }
  };

  const handlePublish = async (agent: AgentRecord) => {
    try {
      await api.publishAgent(agent.id, 'PUBLIC');
      message.success(`"${agent.name}" is now public`);
      void loadAgents();
    } catch {
      message.error('Failed to publish agent');
    }
  };

  const filtered = agents.filter((a) => {
    if (typeFilter && (a.agent_type || 'TASK') !== typeFilter) return false;
    if (activeTab === 'marketplace') return true;
    if (categoryFilter && a.category !== categoryFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.short_description.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const renderAgentCard = (agent: AgentRecord) => {
    const actionButtons = activeTab === 'marketplace'
      ? [
          { key: 'install', label: 'Install', icon: <ShopOutlined />, onClick: () => handleSubscribe(agent) },
          { key: 'fork', label: 'Fork', icon: <BranchesOutlined />, onClick: () => handleFork(agent) },
        ]
      : [
          { key: 'execute', label: 'Execute', icon: <PlayCircleOutlined />, onClick: () => setDetailAgentId(agent.id) },
          { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => { setEditingAgent(agent); setShowBuilder(true); } },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () =>
              Modal.confirm({
                title: `Delete "${agent.name}"?`,
                content: 'This action cannot be undone.',
                okType: 'danger',
                onOk: () => handleDelete(agent),
                className: 'praxys-dark-confirm-modal',
              }),
          },
        ];

    return (
    <Col xs={24} sm={12} lg={8} xl={6} key={agent.id}>
      <Card
        hoverable
        size="small"
        className="praxys-agent-card"
        style={{
          height: '100%',
        }}
        bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <div
            style={{
              minWidth: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#1e1e1e',
              borderRadius: 8,
              flexShrink: 0,
            }}
          >
            {renderAgentIcon(agent.icon, 22)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Text strong style={{ fontSize: 14, color: '#ececec' }}>{agent.name}</Text>
              {agent.featured && <StarFilled style={{ color: '#f59e0b', fontSize: 12 }} />}
              {VISIBILITY_ICON[agent.visibility]}
            </div>
            <div>
              <Tag color={TYPE_COLORS[(agent.agent_type || 'TASK') as AgentType]} style={{ fontSize: 11 }}>
                {TYPE_LABELS[(agent.agent_type || 'TASK') as AgentType]}
              </Tag>
              <Tag color={CATEGORY_COLORS[agent.category] || 'default'} style={{ fontSize: 11 }}>
                {CATEGORY_LABELS[agent.category] || agent.category}
              </Tag>
              {agent.published && <Tag color="green" style={{ fontSize: 11 }}>Published</Tag>}
            </div>
          </div>
        </div>

        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ color: '#94a3b8', fontSize: 12, flex: 1, marginBottom: 8 }}
        >
          {agent.short_description || agent.description || 'No description'}
        </Paragraph>

        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          <span><PlayCircleOutlined /> {agent.execution_count.toLocaleString()} runs</span>
          {(agent.knowledge_base_ids || []).length > 0 && (
            <span><BookOutlined /> {(agent.knowledge_base_ids || []).length} KB{(agent.knowledge_base_ids || []).length !== 1 ? 's' : ''}</span>
          )}
          {(agent.tool_ids || []).length > 0 && (
            <span><ToolOutlined /> {(agent.tool_ids || []).length} tool{(agent.tool_ids || []).length !== 1 ? 's' : ''}</span>
          )}
          {activeTab === 'marketplace' && (
            <span><UserOutlined /> {agent.subscriber_count.toLocaleString()} installed</span>
          )}
          {agent.published && (
            <span><StarFilled style={{ color: '#38bdf8' }} /> {(agent.rating_average || 0).toFixed(1)} ({agent.rating_count || 0})</span>
          )}
          <span style={{ marginLeft: 'auto' }}>v{agent.version}</span>
        </div>

        <div className="praxys-agent-card-actions">
          <Button
            size="small"
            className="praxys-agent-pill-btn"
            icon={<EyeOutlined />}
            onClick={() => setDetailAgentId(agent.id)}
          >
            View
          </Button>
          {actionButtons.map((action) => (
            <Button
              key={action.key}
              size="small"
              className={`praxys-agent-pill-btn${action.danger ? ' praxys-agent-pill-btn--danger' : ''}`}
              icon={action.icon}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
          {activeTab === 'my' && !agent.published && (
            <Button
              size="small"
              className="praxys-agent-pill-btn praxys-agent-pill-btn--accent"
              icon={<GlobalOutlined />}
              onClick={() => handlePublish(agent)}
            >
              Publish
            </Button>
          )}
        </div>
      </Card>
    </Col>
    );
  };

  if (detailAgentId) {
    return (
      <AgentDetailView
        agentId={detailAgentId}
        userId={userId}
        projectFolders={projectFolders}
        onPrepareProjectFolders={onPrepareProjectFolders}
        onBack={() => setDetailAgentId(null)}
        onEdit={(agent) => { setEditingAgent(agent); setShowBuilder(true); setDetailAgentId(null); }}
        onOpenTask={onOpenTask}
      />
    );
  }

  return (
    <div className="praxys-agents-page" style={{ padding: '20px 24px', height: '100%', overflowY: 'auto', background: '#171717' }}>
      {/* Header */}
      <div data-help="agent-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#ececec' }}>
            <RocketOutlined style={{ marginRight: 8, color: '#10a37f' }} />
            Agents
          </Title>
          <Text style={{ color: '#94a3b8', fontSize: 13 }}>
            Build, publish, and run Knowledge, Task, Workflow, and API agents
          </Text>
        </div>
        {activeTab !== 'marketplace' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            data-help="agent-create"
            onClick={() => { setEditingAgent(null); setShowBuilder(true); }}
          >
            New Agent
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => { setActiveTab(k as any); setSearchText(''); setCategoryFilter(''); setTypeFilter(''); }}
        style={{ marginBottom: 16 }}
        items={[
          { key: 'my', label: <span><UserOutlined /> My Agents</span> },
          { key: 'shared', label: <span><TeamOutlined /> Shared With Me</span> },
          { key: 'marketplace', label: <span><ShopOutlined /> Marketplace</span> },
        ]}
      />

      {/* Filters */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Search agents…"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 240, background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
          allowClear
        />
        <Select
          placeholder="Type"
          value={typeFilter || undefined}
          onChange={(v) => setTypeFilter(v || '')}
          allowClear
          style={{ width: 150 }}
          dropdownStyle={{ background: '#2a2a2a', borderColor: '#3f3f3f' }}
        >
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <Select.Option key={k} value={k}>{v}</Select.Option>
          ))}
        </Select>
        <Select
          placeholder="Category"
          value={categoryFilter || undefined}
          onChange={(v) => setCategoryFilter(v || '')}
          allowClear
          style={{ width: 180 }}
          dropdownStyle={{ background: '#2a2a2a', borderColor: '#3f3f3f' }}
        >
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <Select.Option key={k} value={k}>{v}</Select.Option>
          ))}
        </Select>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadAgents}
          loading={loading}
          style={{ background: '#2a2a2a', borderColor: '#3f3f3f', color: '#ececec' }}
        />
      </Space>

      {/* Agent Grid */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 60 }}>
          <Spin size="large" />
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          description={
            <span style={{ color: '#94a3b8' }}>
              {activeTab === 'marketplace'
                ? 'No published agents found. Be the first to publish!'
                : activeTab === 'shared'
                ? 'No agents have been shared with you yet'
                : 'No agents yet. Click "New Agent" to create your first one!'}
            </span>
          }
          style={{ marginTop: 60 }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map(renderAgentCard)}
        </Row>
      )}

      {/* Agent Builder Wizard */}
      {showBuilder && (
        <AgentBuilderWizard
          visible={showBuilder}
          existingAgent={editingAgent}
          userId={userId}
          onClose={() => { setShowBuilder(false); setEditingAgent(null); }}
          onSaved={() => { setShowBuilder(false); setEditingAgent(null); void loadAgents(); }}
        />
      )}
    </div>
  );
};

export default AgentsView;

